/**
 * The venue map (BON-538).
 *
 * Mapbox — the only map library in the app since phase 1 retired Leaflet and
 * the Google loader. Pins are coloured by the same lookup the roster's dot
 * uses, so the two panels read as one answer.
 *
 * What is new here is FITTING: the map frames whatever is currently on it, so
 * narrowing to a region zooms to that region and clearing the filter pulls back
 * out. The existing maps never fit at all — they open on a fixed view of the
 * continent whatever they are showing, which at metro scope is a map of nothing.
 *
 * The map and the roster POINT AT EACH OTHER: hovering a pin tints its row and
 * hovering a row swells its pin, so a venue can be found in either direction
 * without reading names off a list. Clicking a pin opens that venue.
 *
 * Venues without coordinates are simply absent. That is the decided behaviour,
 * not a gap: the roster still lists them, and the map's own count says how many
 * it is actually showing.
 *
 * The lifecycle lives in `useVenueMap`; what is left here is the container and
 * the no-token fallback.
 */
import { useVenueMap } from './useVenueMap';
import type { ColorOf } from './venue-map-layers';
import type { VenueRosterRow } from './venues-roster-api';

import 'mapbox-gl/dist/mapbox-gl.css';

/**
 * Read at RENDER time, not at module load.
 *
 * A module-level constant is captured when the file is first imported, which is
 * before a test can stub the environment — so the map's own tests passed only
 * on a machine whose `.env` happened to carry a token, and rendered the
 * fallback in CI where none is set. Reading it here costs nothing and makes the
 * no-token path testable from either side.
 */
const mapboxToken = (): string | undefined =>
  import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

export interface VenueMapProps {
  venues: readonly VenueRosterRow[];
  /** The roster's own colour lookup, so a pin and its row agree. */
  colorOf: ColorOf;
  /** Changes only when the map is looking at a different dataset — the map
   *  frames its pins then, and leaves the camera alone otherwise. */
  fitKey: string;
  /** The venue the roster is currently pointing at; its pin swells. */
  hoveredId?: string | null;
  /** Fired with a venue id as the cursor enters a pin, null as it leaves. */
  onHoverVenue?: (id: string | null) => void;
  /** Fired when a pin is clicked. */
  onSelectVenue?: (id: string) => void;
}

export function VenueMap({
  venues,
  colorOf,
  fitKey,
  hoveredId = null,
  onHoverVenue,
  onSelectVenue,
}: VenueMapProps) {
  const token = mapboxToken();
  const container = useVenueMap({
    token,
    venues,
    colorOf,
    fitKey,
    hoveredId,
    onHoverVenue,
    onSelectVenue,
  });

  if (!token) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          textAlign: 'center',
          color: 'var(--nb-subtle)',
          fontSize: 13,
        }}
      >
        Map unavailable — VITE_MAPBOX_TOKEN is not set.
      </div>
    );
  }

  return <div ref={container} style={{ height: '100%', width: '100%' }} />;
}
